import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useClient } from '../context/ClientContext';
import { Button } from '../components/ui/button';
import { RefreshCw, Save, Trash2, Loader2, AlertCircle, TrendingUp, TrendingDown, Wallet } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

/**
 * Mandi Mapping — Phase-1 operator-only screen.
 *
 * The screen lists every invoice item in the client's supplier data, ranked
 * by spend. For each unmapped item we show an AI-lite suggestion (name
 * substring match against a small commodity list). Nothing is bound unless
 * the operator explicitly approves.
 *
 * Language discipline: every ₹ figure on this screen is labelled
 * "negotiation headroom" — never "recoverable".
 */

const COMMODITIES = [
  'Onion', 'Tomato', 'Potato', 'Chilli', 'Garlic', 'Ginger', 'Cabbage',
  'Cauliflower', 'Brinjal', 'Capsicum', 'Coriander', 'Mint', 'Lemon',
  'Coconut', 'Spinach', 'Cucumber', 'Beans', 'Carrot', 'Banana', 'Apple',
  'Mango', 'Rice', 'Wheat', 'Green Peas', 'Pumpkin',
];

export const MandiMapping = () => {
  usePageMeta({ title: 'Mandi Mapping — Epicurea Nexus' });
  const { API, selectedClient } = useClient();
  const [state, setState] = useState({ unmapped: [], mapped: [], snapshot_latest: null, baseline_min_required_days: 60 });
  const [preview, setPreview] = useState({ rows: [], coverage: null, snapshot_latest: null });
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  // Draft form per-item, so the operator can pick commodity + market before saving.
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    if (!selectedClient) return;
    setLoading(true); setError('');
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/mandi-mapping/${selectedClient.id}`),
        axios.get(`${API}/mandi-mapping/${selectedClient.id}/preview?days=30`),
        axios.get(`${API}/mandi-mapping/_health/snapshot`).catch(() => ({ data: null })),
      ]);
      setState(r1.data);
      setPreview(r2.data);
      setHealth(r3.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load mapping state');
    } finally { setLoading(false); }
  }, [selectedClient, API]);

  useEffect(() => { load(); }, [load]);

  const saveMapping = async (item, draft) => {
    setBusyKey(item.item_name); setError('');
    try {
      await axios.post(`${API}/mandi-mapping/${selectedClient.id}`, {
        item_name: item.item_name,
        commodity: draft.commodity,
        market: draft.market,
        variety: draft.variety || '',
      });
      setDrafts((d) => { const n = { ...d }; delete n[item.item_name]; return n; });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally { setBusyKey(null); }
  };

  const removeMapping = async (item) => {
    setBusyKey(item.item_name); setError('');
    try {
      await axios.delete(`${API}/mandi-mapping/${selectedClient.id}/${encodeURIComponent(item.item_name)}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Remove failed');
    } finally { setBusyKey(null); }
  };

  const refreshSnap = async () => {
    setRefreshing(true); setError('');
    try {
      await axios.post(`${API}/mandi-mapping/${selectedClient.id}/snapshot`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Snapshot refresh failed');
    } finally { setRefreshing(false); }
  };

  const setDraft = (item, patch) => {
    setDrafts((d) => ({
      ...d,
      [item.item_name]: {
        commodity: item.suggestion || '',
        market: '',
        variety: '',
        ...(d[item.item_name] || {}),
        ...patch,
      },
    }));
  };

  if (!selectedClient) {
    return <div className="p-6 text-text-muted">Select a client to map their mandi items.</div>;
  }

  return (
    <div className="space-y-6" data-testid="mandi-mapping-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold text-primary tracking-tight">Mandi Mapping</h2>
          <p className="text-text-secondary mt-1">
            Map each invoice item to an Agmarknet commodity + reference market. Unmapped items are silently not benchmarked.
            <span className="block text-text-muted text-xs mt-1">
              Every ₹ figure below is <strong>negotiation headroom</strong>, not recoverable money. Restaurants pay <em>delivered</em> prices — a wholesale-gate modal is not a like-for-like comparison. The rule flags the <strong>shift vs this client's own baseline</strong>, never the raw level.
            </span>
          </p>
          {state.snapshot_latest && (
            <p className="text-xs text-text-muted mt-1">Prices last snapshotted {new Date(state.snapshot_latest).toLocaleString()}</p>
          )}
        </div>
        <Button onClick={refreshSnap} disabled={refreshing} className="bg-primary hover:bg-primary-hover" data-testid="mandi-refresh-snapshot">
          {refreshing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Fetching…</> : <><RefreshCw className="h-4 w-4 mr-1" /> Refresh snapshot</>}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-state-error rounded-md p-3 text-sm text-state-error flex items-start gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {preview.coverage && (
        <div className="bg-white border border-border rounded-md p-4">
          <div className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">Coverage · last 30 days</div>
          <div className="text-sm text-text-secondary">
            Benchmarked <strong>{preview.coverage.benchmarked}</strong> of <strong>{preview.coverage.total_lines}</strong> invoice lines.
            {preview.coverage.total_lines > 0 && (
              <span className="ml-2 text-text-muted">
                ({Math.round(100 * preview.coverage.benchmarked / preview.coverage.total_lines)}%)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Nightly snapshot health — plain-words read-out for the operator */}
      {health && (
        <div className="bg-white border border-border rounded-md p-4" data-testid="snapshot-health">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">Nightly snapshot health</div>
              {health.last_run ? (
                <div className="text-sm text-text-secondary">
                  Last run <strong>{new Date(health.last_run.finished_at).toLocaleString()}</strong>{' '}
                  · <strong>{health.last_run.upserted}</strong> new rows across{' '}
                  <strong>{health.last_run.targets}</strong> mapped target(s)
                  {health.last_run.failed_targets > 0 && (
                    <span className="text-amber-800"> · {health.last_run.failed_targets} target(s) skipped</span>
                  )}
                  <span className="text-text-muted"> · {health.last_run.clients} client(s)</span>
                </div>
              ) : (
                <div className="text-sm text-text-secondary italic">
                  Nightly job has not run yet. It runs once daily; use "Refresh snapshot" above to fetch on-demand.
                </div>
              )}
              <div className="text-[11px] text-text-muted mt-1">
                {health.real_snapshots_total.toLocaleString()} real snapshots persisted
                · {health.real_snapshots_today} added today
                · baseline requires ≥{health.baseline_min_required_days} real days per (commodity, market)
                {health.backfilled_snapshots_total > 0 && (
                  <span> · {health.backfilled_snapshots_total} demo-only backfilled rows (ignored by the math)</span>
                )}
              </div>
            </div>
            {health.last_run && (health.last_run.errors || []).length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-amber-800 hover:underline">
                  {health.last_run.errors.length} skipped target(s) — details
                </summary>
                <ul className="mt-2 space-y-0.5 text-[11px] text-text-secondary max-w-xl">
                  {health.last_run.errors.slice(0, 8).map((e, i) => (
                    <li key={i} className="font-mono">• {e}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}

      {/* MAPPED items with live preview */}
      {state.mapped.length > 0 && (
        <div className="bg-white border border-border rounded-md">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-primary">Mapped items</h3>
          </div>
          <table className="w-full text-sm" data-testid="mapped-items-table">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-text-muted">
              <tr>
                <th className="text-left px-4 py-2">Item</th>
                <th className="text-left px-4 py-2">→ Agmarknet commodity · market</th>
                <th className="text-left px-4 py-2">State</th>
                <th className="text-right px-4 py-2">Headroom (30d)</th>
                <th className="text-right px-4 py-2">Baseline coverage</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {state.mapped.map((it) => {
                const p = preview.rows.find((r) => r.item_name === it.item_name);
                const stateKey = p?.state || (it.mapping?.baseline_ready ? 'ready' : 'baseline_building');
                const stateLabel = {
                  ready: { text: 'Ready', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
                  baseline_building: { text: 'Baseline building', tone: 'bg-amber-50 text-amber-800 border-amber-200' },
                  needs_review: { text: 'Needs review — implausible premium', tone: 'bg-red-50 text-red-800 border-red-200' },
                }[stateKey] || { text: stateKey, tone: 'bg-neutral-50 text-neutral-700 border-neutral-200' };
                const days = it.mapping?.baseline_real_days ?? p?.baseline_real_days ?? 0;
                const need = it.mapping?.baseline_min_required ?? state.baseline_min_required_days ?? 60;
                const win = it.mapping?.baseline_window_days ?? p?.baseline_window_days ?? 90;
                return (
                  <tr key={it.item_name} className="border-t border-border" data-testid={`mapped-row-${it.item_name}`}>
                    <td className="px-4 py-2 font-medium">
                      {it.item_name} <span className="text-xs text-text-muted">({it.unit || '?'})</span>
                      {it.mapping?.market_mismatch_warning && (
                        <div className="text-[11px] text-amber-800 mt-1 flex items-start gap-1" data-testid={`mismatch-warning-${it.item_name}`}>
                          <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                          <span>{it.mapping.market_mismatch_warning}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-text-secondary">{it.mapping?.commodity} · {it.mapping?.market}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded border ${stateLabel.tone}`}
                            data-testid={`state-badge-${it.item_name}`}>
                        {stateLabel.text}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {stateKey === 'ready' && p && p.monthly_impact_inr > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-800">
                          <Wallet className="h-3.5 w-3.5" />
                          ₹{p.monthly_impact_inr.toLocaleString('en-IN')}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2 text-right text-xs">
                      <span className={days >= need ? 'text-emerald-700' : 'text-amber-700'}>
                        {days} of {win} real days
                      </span>
                      {days < need && (
                        <div className="text-[10px] text-text-muted">need ≥{need}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => removeMapping(it)} disabled={busyKey === it.item_name}
                              className="text-state-error hover:bg-red-50 p-1 rounded"
                              data-testid={`remove-mapping-${it.item_name}`}
                              title="Remove mapping">
                        {busyKey === it.item_name ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* UNMAPPED items with suggestions */}
      <div className="bg-white border border-border rounded-md">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold text-primary">Unmapped items — ranked by spend</h3>
          <p className="text-xs text-text-muted mt-1">
            Anything below stays silently un-benchmarked until you approve a mapping. Items in non-weight units (pieces, bunches) are simply not eligible — mark shows below.
          </p>
        </div>
        <table className="w-full text-sm" data-testid="unmapped-items-table">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-text-muted">
            <tr>
              <th className="text-left px-4 py-2">Item</th>
              <th className="text-right px-4 py-2">Spend</th>
              <th className="text-right px-4 py-2">Invoices</th>
              <th className="text-left px-4 py-2">Agmarknet commodity</th>
              <th className="text-left px-4 py-2">Reference market</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {state.unmapped.length === 0 && (
              <tr><td colSpan={6} className="text-center text-xs text-text-muted p-4">Everything is mapped. </td></tr>
            )}
            {state.unmapped.slice(0, 30).map((it) => {
              const d = drafts[it.item_name] || { commodity: it.suggestion || '', market: '' };
              return (
                <tr key={it.item_name} className="border-t border-border" data-testid={`unmapped-row-${it.item_name}`}>
                  <td className="px-4 py-2 font-medium">
                    {it.item_name}
                    <div className="text-xs text-text-muted">unit: {it.unit || '—'} {!it.unit_supported && <span className="text-amber-700">· not benchmarkable</span>}</div>
                  </td>
                  <td className="px-4 py-2 text-right font-semibold">₹{it.spend.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2 text-right text-text-muted">{it.invoices}</td>
                  <td className="px-4 py-2">
                    <select
                      className="border border-border rounded-md px-2 py-1 text-sm w-full"
                      value={d.commodity}
                      onChange={(e) => setDraft(it, { commodity: e.target.value })}
                      disabled={!it.unit_supported}
                      data-testid={`commodity-select-${it.item_name}`}
                    >
                      <option value="">— pick commodity —</option>
                      {COMMODITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {it.suggestion && <div className="text-xs text-text-muted mt-1">Suggested: {it.suggestion}</div>}
                  </td>
                  <td className="px-4 py-2">
                    <input
                      className="border border-border rounded-md px-2 py-1 text-sm w-full"
                      placeholder="e.g. Pune(Khadiki) APMC"
                      value={d.market}
                      onChange={(e) => setDraft(it, { market: e.target.value })}
                      disabled={!it.unit_supported}
                      data-testid={`market-input-${it.item_name}`}
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="sm"
                      className="bg-primary text-white"
                      disabled={busyKey === it.item_name || !d.commodity || !d.market || !it.unit_supported}
                      onClick={() => saveMapping(it, d)}
                      data-testid={`save-mapping-${it.item_name}`}
                    >
                      {busyKey === it.item_name ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Map</>}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="text-xs text-text-muted flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      )}
    </div>
  );
};

export default MandiMapping;
